import {
  type FeatureDataResponse,
  type FeatureSchema,
  FeaturesApiFactory,
  FeaturesSchemasApiFactory,
} from '@mynt/backoffice-api-spec/features'
import Axios from 'api/axios'
import config from 'config'
import { mapFeatureDataAndSchemaResponseToFeatureData, mapFeatureSchemaResponseToFeatureData } from '../mappers'
import type { FeatureData, FeatureRequest } from '../types'
import { mapNullableFormFieldsToFeatureRequest } from './mappers'

const featuresApi = FeaturesApiFactory(undefined, config.boBaseUrl, Axios)
const schemasApi = FeaturesSchemasApiFactory(undefined, config.boBaseUrl, Axios)

export const getFeatures = async (): Promise<FeatureData[]> => {
  return await schemasApi.listFeatureSchemas().then(({ data }) => data.map(mapFeatureSchemaResponseToFeatureData))
}

const mergeFeatureSchemaData = (data: FeatureDataResponse[], schemas: FeatureSchema[]): FeatureData[] => {
  return schemas
    .map((featureSchema) => {
      const featureItem = data.find((item) => item.id === featureSchema.featureId)

      if (!featureItem) {
        return null
      }

      return mapFeatureDataAndSchemaResponseToFeatureData({ featureSchemaResponse: featureSchema, featureDataResponse: featureItem })
    })
    .filter((feature) => feature !== null) as FeatureData[]
}

export const getFeaturesForTemplate = async (templateId: string): Promise<FeatureData[]> => {
  const featureItems = await featuresApi.allFeaturesForTemplate(templateId).then(({ data }) => data)
  const featureSchemas = await schemasApi.getFeatureSchemasForTemplate(templateId).then(({ data }) => data)

  return mergeFeatureSchemaData(featureItems, featureSchemas)
}

interface GetFeaturesForCustomerParams {
  customerId: string
}

export const getOverriddenFeaturesForCustomer = async ({ customerId }: GetFeaturesForCustomerParams): Promise<FeatureData[]> => {
  const customerFeatureItems = await featuresApi.allFeaturesForCustomer(customerId).then(({ data }) => data as FeatureDataResponse[])
  const featureSchemas = await schemasApi.listFeatureSchemas().then(({ data }) => data)

  return mergeFeatureSchemaData(customerFeatureItems, featureSchemas)
}

export const getEffectiveFeaturesForCustomer = async ({ customerId }: GetFeaturesForCustomerParams): Promise<FeatureData[]> => {
  const effectiveFeatureItems = await featuresApi
    .allEffectiveFeaturesForCustomer(customerId)
    .then(({ data }) => data as FeatureDataResponse[])

  const featureSchemas = await schemasApi.listFeatureSchemas().then(({ data }) => data)

  console.log({ effectiveFeatureItems, featureSchemas })

  return mergeFeatureSchemaData(effectiveFeatureItems, featureSchemas)
}

export const saveFeatureForTemplate = async (templateId: string, featureRequest: FeatureRequest<null>) => {
  return featuresApi.setFeatureForTemplate(featureRequest.id, templateId, mapNullableFormFieldsToFeatureRequest(featureRequest).data)
}

interface SaveFeatureForCustomerRequest {
  customerId: string
  featureId: string
  featureRequest: FeatureRequest<null>
}

export const saveFeatureForCustomer = ({ customerId, featureId, featureRequest }: SaveFeatureForCustomerRequest) => {
  return featuresApi.setFeatureForCustomer(featureId, customerId, mapNullableFormFieldsToFeatureRequest(featureRequest).data)
}

export const deleteFeatureForCustomer = ({ customerId, featureId }: { customerId: string; featureId: string }) => {
  return featuresApi.deleteFeatureForCustomer(featureId, customerId)
}
